import { PenLine, Clock, Mail, User as UserIcon } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'

import { extractApiError, FormModal } from '@components/branded-modal'
import { StatusInfo } from '@components/status-info'
import { UserForm } from '@components/user'
import { AdasBox, AdasButton, AdasCard, AdasDivider, AdasTypography } from '@components/wrapper-components'
import { resendUserInvitation } from '../../../api/api'
import { Logger } from '../../../logger'
import { getRole, isClientUser, phoneNumberFormat } from '../../../utils/utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export const UserInfo = (props) => {
  const {
    open = true,
    userInfo,
    closeDrawer,
    updateClientListHandler,
    global = false,
    userCenters = [],
    allowDelete = false,
    clientName,
    userKind,
    clients
  } = props
  const [updatedUserInfo, setUpdatedUserInfo] = useState(userInfo)
  const [isUserEdit, setIsUserEdit] = useState(false)
  const [resendState, setResendState] = useState({ sending: false, notice: null, error: null })

  // Invited but not set up yet: offer a fresh set-up email (CAR-3869). Same for every role.
  const canResend = !global && !!updatedUserInfo?.is_active && !!updatedUserInfo?.setup_pending
  const handleResend = useCallback(async () => {
    setResendState({ sending: true, notice: null, error: null })
    try {
      await resendUserInvitation({ userId: updatedUserInfo.id })
      setResendState({ sending: false, notice: `A new set-up email was sent to ${updatedUserInfo.email}.`, error: null })
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'userInfo', method: 'handleResend', userId: updatedUserInfo?.id } })
      setResendState({ sending: false, notice: null, error: extractApiError(error) || 'Could not resend the set-up email.' })
    }
  }, [updatedUserInfo?.id, updatedUserInfo?.email])
  const onSaveClientSuccessHandler = (props) => {
    setUpdatedUserInfo(props)
    updateClientListHandler()
    setIsUserEdit(false)
    closeDrawer()
  }

  const onDeleteClientSuccessHandler = () => {
    updateClientListHandler()
    closeDrawer()
  }

  const RecordInformation = ({ lastModified }) => {
    const formattedDate = lastModified ? new Date(lastModified).toLocaleDateString() : ''
    const formattedTime = lastModified
      ? new Date(lastModified).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      : ''

    return (
      <AdasBox>
        <AdasTypography variant='overline' className='font-semibold text-[#6b7280]'>
          RECORD INFORMATION
        </AdasTypography>

        <AdasCard className='flex p-2 rounded-md border-l-4 border-[#b7bec8] bg-[#fff]'>
          <Clock style={{ width: '20px', height: '20px', color: '#6b7280', marginRight: '8px' }} />
          <AdasTypography variant='body2' className='italic text-[#4b5563]'>
            <strong>Last Modified:</strong> {formattedDate} @ {formattedTime}
          </AdasTypography>
        </AdasCard>
      </AdasBox>
    )
  }

  if (isUserEdit) {
    return (
      <UserForm
        open={open}
        allowDelete={allowDelete}
        isAddUser={false}
        isEdit={isUserEdit}
        userData={userInfo}
        close={() => setIsUserEdit(false)}
        onSaveSuccess={onSaveClientSuccessHandler}
        onDeleteSuccess={onDeleteClientSuccessHandler}
        global={global}
        userKind={userKind}
        clients={clients}
      />
    )
  }

  const footer = (
    <AdasBox className='flex items-center justify-end gap-2'>
      {canResend && (
        <AdasButton startIcon={<Mail />} aria-label='Resend set-up email' disabled={resendState.sending} onClick={handleResend}>
          Resend set-up email
        </AdasButton>
      )}
      <AdasButton startIcon={<PenLine />} aria-label='Edit' color='primary' onClick={() => setIsUserEdit(true)}>
        Edit
      </AdasButton>
    </AdasBox>
  )

  const statusLabel = !updatedUserInfo?.is_active ? 'Inactive' : updatedUserInfo?.setup_pending ? 'Pending set-up' : 'Active'

  return (
    <FormModal
      open={open}
      onClose={closeDrawer}
      title='User Info'
      icon={UserIcon}
      footer={footer}
      errorMessage={resendState.error}
      onDismissError={() => setResendState((prev) => ({ ...prev, error: null }))}
    >
      {resendState.notice && (
        <div role='status' className='mb-3 rounded-lg border border-[#86EFAC] bg-[#F0FDF4] px-3 py-2 text-[13px] text-[#166534]'>
          {resendState.notice}
        </div>
      )}
      <AdasCard className='adas-card'>
        <span>
          <AdasTypography className='font-semibold inline' variant='h6'>
            {updatedUserInfo.name}
          </AdasTypography>
          <StatusInfo color={updatedUserInfo.is_active ? 'primary' : 'default'} label={statusLabel} className='float-right' />
        </span>
        <AdasDivider className='my-2 mx-0' />
        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Role
        </AdasTypography>
        <AdasTypography>{getRole(updatedUserInfo)}</AdasTypography>
        {isClientUser(updatedUserInfo) && (
          <>
            <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
              Client
            </AdasTypography>
            <AdasTypography>{clientName || '—'}</AdasTypography>
          </>
        )}
        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Email
        </AdasTypography>
        <AdasTypography>
          <a href={`mailto:${updatedUserInfo.email}`}>{updatedUserInfo.email}</a>
        </AdasTypography>
        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Phone
        </AdasTypography>

        <AdasTypography>
          <a href={`tel:${updatedUserInfo.phone}`}>{phoneNumberFormat(updatedUserInfo.phone)}</a>
        </AdasTypography>
        <AdasDivider className='my-2 mx-0' />
        <RecordInformation lastModified={updatedUserInfo.updated_at ? updatedUserInfo.updated_at : updatedUserInfo.created_at} />
        {userCenters.length > 0 && (
          <AdasBox>
            <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
              Center(s)
            </AdasTypography>
            <ul>
              {userCenters.map((center) => (
                <li key={center.id}>{center.name}</li>
              ))}
            </ul>
          </AdasBox>
        )}
      </AdasCard>
    </FormModal>
  )
}

UserInfo.propTypes = {
  open: PropTypes.bool,
  userInfo: PropTypes.object,
  closeDrawer: PropTypes.func.isRequired,
  updateClientListHandler: PropTypes.func.isRequired,
  global: PropTypes.bool,
  userCenters: PropTypes.array,
  allowDelete: PropTypes.bool,
  clientName: PropTypes.string,
  userKind: PropTypes.oneOf(['client']),
  clients: PropTypes.array
}
